import * as yup from 'yup';
import { Field, FIELD_TYPES, UIschema } from '../../../types';
import svStrings from '../../../sv-strings';
import { SUPPORTED_LEAGUES } from '../../../types/app-config';
import { PROMO_VARIANT } from './types';
import { Poll } from '../../scenes/polls/types';
import moment from 'moment';

const leagueSeasons = [
  (new Date().getFullYear() - 1).toString(),
  new Date().getFullYear().toString(),
  (new Date().getFullYear() + 1).toString(),
  '',
];

const createPromoFields = (teamAbbrv: string, num: number, polls: Poll[]) => {
  const fields: Field[] = [
    {
      title: svStrings.fields.promoImage,
      id: `teampage-promo-image-${num}`,
      type: FIELD_TYPES.URL,
      key: `promos[${num}].contentUrl`,
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.promoVariant,
      id: `teampage-promo-variant-${num}`,
      type: FIELD_TYPES.SELECT,
      key: `promos[${num}].promo_type`,
      value: PROMO_VARIANT.NONE,
      validation: yup
        .string()
        .oneOf(
          [
            PROMO_VARIANT.IMAGE,
            PROMO_VARIANT.SUBSCRIPTION,
            PROMO_VARIANT.FORZASYS_VIDEO,
            PROMO_VARIANT.POLL,
            PROMO_VARIANT.FLARIE,
            PROMO_VARIANT.NONE,
          ],
          svStrings.validations.mustBePromoValues
        ),
      options: [
        PROMO_VARIANT.IMAGE,
        PROMO_VARIANT.SUBSCRIPTION,
        PROMO_VARIANT.FORZASYS_VIDEO,
        PROMO_VARIANT.POLL,
        PROMO_VARIANT.FLARIE,
        PROMO_VARIANT.NONE,
      ],
      optionsText: [
        svStrings.fields.promoVariantImage,
        svStrings.fields.promoVariantSubscription,
        svStrings.fields.promoVariantVideo,
        svStrings.fields.promoVariantPoll,
        svStrings.fields.promoVariantFlarie,
        svStrings.fields.promoVariantNone,
      ],
    },
    {
      title: svStrings.fields.promoLink,
      id: `teampage-promo-link-${num}`,
      type: FIELD_TYPES.URL,
      key: `promos[${num}].targetUrl`,
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.promoOpen,
      id: `teampage-open-in-app-${num}`,
      type: FIELD_TYPES.SWITCH,
      key: `promos[${num}].promo_open_inapp`,
      value: false,
      validation: yup.boolean(),
    },
    {
      title: svStrings.fields.promoWidth,
      id: `teampage-promo-width-${num}`,
      type: FIELD_TYPES.NUMBER,
      key: `promos[${num}].width`,
      value: 0,
      validation: yup.number(),
    },
    {
      title: svStrings.fields.promoHeight,
      id: `teampage-promo-height-${num}`,
      type: FIELD_TYPES.NUMBER,
      key: `promos[${num}].height`,
      value: 0,
      validation: yup.number(),
    },
    {
      title: svStrings.fields.promoTitle,
      id: `teampage-promo-title-${num}`,
      type: FIELD_TYPES.TEXT,
      key: `promos[${num}].text`,
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.promoPoll,
      id: `teampage-promo-poll-id-${num}`,
      type: FIELD_TYPES.SELECT,
      key: `promos[${num}].pollId`,
      value: '',
      options: [
        ...(polls
          ?.sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())
          .map((poll) => poll.id) || []),
        '',
      ],
      optionsText: [
        ...(polls
          ?.sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())
          .map((poll) => poll.name) || []),
        svStrings.fields.promoPollNone,
      ],
      validation: yup
        .string()
        .oneOf(
          [...(polls ? polls.map((poll) => poll.id) : []), ''],
          svStrings.validations.mustSelectAlternative
        ),
    },
    // Disabled
    // {
    //  title: svStrings.fields.promoRestriction,
    //  id: `teampage-age-restriction-${num}`,
    //  type: FIELD_TYPES.SWITCH,
    //  key: `promos[${num}].promo_age_restriction`,
    //  value: '',
    //  validation: yup.boolean(),
    // },
  ];

  return fields;
};

export const createPromos = (teamAbbrv: string, amount: number, polls: Poll[]) => {
  return Array.from(Array(amount)).map((_, index) => {
    const promo = {
      type: FIELD_TYPES.PROMO_FIELD,
      id: `promo-${index}`,
      fields: createPromoFields(teamAbbrv, index, polls),
    };

    return promo;
  });
};

const createTeamInfoFields = () => {
  return [
    {
      title: svStrings.fields.teamSquadImage,
      id: 'squad-image',
      type: FIELD_TYPES.IMAGE,
      key: 'squadImage',
      value: '',
      isTeamDataValue: true,
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.teamLogo,
      id: 'team-logo',
      type: FIELD_TYPES.IMAGE,
      key: 'logoImageUrl',
      value: '',
      isTeamDataValue: true,
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.teamHeaderBackground,
      id: 'header-background',
      type: FIELD_TYPES.IMAGE,
      key: 'headerImageUrl',
      value: '',
      isTeamDataValue: true,
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.teamArenaBarcode,
      id: 'arena-barcode',
      type: FIELD_TYPES.SELECT,
      key: 'ticketType',
      value: '',
      isTeamDataValue: true,
      validation: yup
        .string()
        .oneOf(['aztec', 'code128', 'qr', 'pdf417', ''], svStrings.validations.mustBeBarcodeValues),
      options: ['aztec', 'code128', 'qr', 'pdf417', ''],
      optionsText: ['Aztec Code', 'Code 128', 'QR', 'PDF417', ''],
    },
    {
      title: svStrings.fields.appStoreLink,
      id: 'app-store-link',
      type: FIELD_TYPES.URL,
      key: 'itunesUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.googlePlayLink,
      id: 'google-play-link',
      type: FIELD_TYPES.URL,
      key: 'gplayUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.appPromoImage,
      id: 'application-promo-image',
      type: FIELD_TYPES.IMAGE,
      key: 'appPromoImg',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.appPromoText,
      id: 'application-promo-text',
      type: FIELD_TYPES.TEXT,
      key: 'appPromoText',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.currentSeason,
      id: 'current-season',
      type: FIELD_TYPES.SELECT,
      key: 'currentSeason',
      value: '',
      superAdminOnly: true,
      validation: yup.string().required(svStrings.validations.isRequired),
      options: Object.values(leagueSeasons),
    },
    {
      title: svStrings.fields.leagueName,
      id: 'config-league',
      type: FIELD_TYPES.SELECT,
      key: 'configLeagueName',
      value: '',
      superAdminOnly: true,
      validation: yup
        .string()
        .oneOf(Object.values(SUPPORTED_LEAGUES), svStrings.validations.isRequired),
      options: Object.values(SUPPORTED_LEAGUES),
    },
    {
      title: svStrings.fields.teamAbbrv,
      id: 'team-abbreviation',
      type: FIELD_TYPES.TEXT,
      key: 'abbrv',
      value: '',
      isTeamDataValue: true,
      isReadOnly: true,
      validation: yup.string().required(svStrings.validations.isRequired),
    },
    {
      title: svStrings.fields.teamName,
      id: 'team-name',
      type: FIELD_TYPES.TEXT,
      key: 'name',
      value: '',
      isTeamDataValue: true,
      isReadOnly: true,
      validation: yup
        .string()
        .min(3, svStrings.validations.mustBeAtleast3Chars)
        .required(svStrings.validations.isRequired),
    },
    {
      title: svStrings.fields.appName,
      id: 'application-name',
      type: FIELD_TYPES.TEXT,
      key: 'name',
      value: '',
      superAdminOnly: true,
      isReadOnly: true,
      validation: yup.string(),
    },
    {
      title: svStrings.fields.linksTV4,
      id: 'team-link-tv4',
      type: FIELD_TYPES.TEXT,
      key: 'tv4',
      value: '',
      isTeamDataValue: true,
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.linksPolicy,
      id: 'team-link-url-policy',
      type: FIELD_TYPES.TEXT,
      key: 'urlPolicy',
      value: '',
      isTeamDataValue: true,
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.teamInfoAbbrvAliases,
      id: 'team-info-abbrv-aliases',
      type: FIELD_TYPES.TEXT,
      key: 'abbrvAliases',
      value: '',
      superAdminOnly: true,
      isTeamDataValue: true,
      validation: yup.string(),
    },
  ];
};

const createAppThemeFields = () => {
  return [
    {
      title: svStrings.fields.themeIdMain,
      id: 'ID_MAIN',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.ID_MAIN',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeIdForeground,
      id: 'ID_FOREGROUND',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.ID_FOREGROUND',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeIdContrast,
      id: 'ID_CONTRAST',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.ID_CONTRAST',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeComponentBackground,
      id: 'COMPONENT_BACKGROUND',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.COMPONENT_BACKGROUND',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeComponentForeground,
      id: 'COMPONENT_FOREGROUND',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.COMPONENT_FOREGROUND',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeComponentBorder,
      id: 'COMPONENT_BORDER',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.COMPONENT_BORDER',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeComponentForegroundId,
      id: 'COMPONENT_FOREGROUND_ID',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.COMPONENT_FOREGROUND_ID',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeButtonBackground,
      id: 'BUTTON_BACKGROUND',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.BUTTON_BACKGROUND',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeButtonForeground,
      id: 'BUTTON_FOREGROUND',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.BUTTON_FOREGROUND',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeSocialHeart,
      id: 'SOCIAL_HEART',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.SOCIAL_HEART',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeTopBarOverride,
      id: 'topBarOverride',
      type: FIELD_TYPES.SWITCH,
      key: 'appThemeV2.topBarOverride',
      value: false,
      validation: yup.boolean(),
    },
    {
      title: svStrings.fields.themeTopBarBackground,
      id: 'TOP_BAR_BACKGROUND',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.TOP_BAR_BACKGROUND',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeTopBarForeground,
      id: 'TOP_BAR_FOREGROUND',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.TOP_BAR_FOREGROUND',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.themeTopBarContrast,
      id: 'TOP_BAR_CONTRAST',
      type: FIELD_TYPES.COLOR_PICKER,
      key: 'appThemeV2.palette.TOP_BAR_CONTRAST',
      value: '',
      validation: yup.string(),
    },
  ];
};

const createTeamLinkFields = () => {
  return [
    {
      title: svStrings.fields.linksHomePageLink,
      id: 'team-homepage-link',
      type: FIELD_TYPES.URL,
      key: 'links[0].targetUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.linksHomePageIcon,
      id: 'team-homepage-icon',
      type: FIELD_TYPES.IMAGE,
      key: 'links[0].imageUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.linksShopLink,
      id: 'team-shop-link',
      type: FIELD_TYPES.URL,
      key: 'links[1].targetUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.linksShopIcon,
      id: 'team-shop-icon',
      type: FIELD_TYPES.IMAGE,
      key: 'links[1].imageUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.linksPlayLink,
      id: 'team-play-link',
      type: FIELD_TYPES.URL,
      key: 'links[2].targetUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.linksPlayIcon,
      id: 'team-play-icon',
      type: FIELD_TYPES.IMAGE,
      key: 'links[2].imageUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.linksFacebookLink,
      id: 'team-facebook-link',
      type: FIELD_TYPES.URL,
      key: 'links[3].targetUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.linksFacebookIcon,
      id: 'team-facebook-icon',
      type: FIELD_TYPES.IMAGE,
      key: 'links[3].imageUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.linksInstagramLink,
      id: 'team-instagram-link',
      type: FIELD_TYPES.URL,
      key: 'links[4].targetUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.linksInstagramIcon,
      id: 'team-instagram-icon',
      type: FIELD_TYPES.IMAGE,
      key: 'links[4].imageUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.linksTwitterLink,
      id: 'team-twitter-link',
      type: FIELD_TYPES.URL,
      key: 'links[5].targetUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validUrl),
    },
    {
      title: svStrings.fields.linksTwitterIcon,
      id: 'team-twitter-icon',
      type: FIELD_TYPES.IMAGE,
      key: 'links[5].imageUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
  ];
};

const createInfoFields = () => {
  return [
    {
      type: FIELD_TYPES.INFO_FIELDS,
      id: 'info-column-0',
      fields: [
        {
          title: '#1 ' + svStrings.fields.teamInfoName,
          id: 'info-name-0',
          type: FIELD_TYPES.TEXT,
          key: 'info[0].name',
          value: '',
          validation: yup.string(),
        },
        {
          title: svStrings.fields.teamInfoValue,
          id: 'info-value-0',
          type: FIELD_TYPES.TEXT,
          key: 'info[0].value',
          value: '',
          validation: yup.string(),
        },
      ],
    },
    {
      type: FIELD_TYPES.INFO_FIELDS,
      id: 'info-column-1',
      fields: [
        {
          title: '#2 ' + svStrings.fields.teamInfoName,
          id: 'info-name-1',
          type: FIELD_TYPES.TEXT,
          key: 'info[1].name',
          value: '',
          validation: yup.string(),
        },
        {
          title: svStrings.fields.teamInfoValue,
          id: 'info-value-1',
          type: FIELD_TYPES.TEXT,
          key: 'info[1].value',
          value: '',
          validation: yup.string(),
        },
      ],
    },
    {
      type: FIELD_TYPES.INFO_FIELDS,
      id: 'info-column-2',
      fields: [
        {
          title: '#3 ' + svStrings.fields.teamInfoName,
          id: 'info-name-2',
          type: FIELD_TYPES.TEXT,
          key: 'info[2].name',
          value: '',
          validation: yup.string(),
        },
        {
          title: svStrings.fields.teamInfoValue,
          id: 'info-value-2',
          type: FIELD_TYPES.TEXT,
          key: 'info[2].value',
          value: '',
          validation: yup.string(),
        },
      ],
    },
    {
      type: FIELD_TYPES.INFO_FIELDS,
      id: 'info-column-3',
      fields: [
        {
          title: '#4 ' + svStrings.fields.teamInfoName,
          id: 'info-name-3',
          type: FIELD_TYPES.TEXT,
          key: 'info[3].name',
          value: '',
          validation: yup.string(),
        },
        {
          title: svStrings.fields.teamInfoValue,
          id: 'info-value-3',
          type: FIELD_TYPES.TEXT,
          key: 'info[3].value',
          value: '',
          validation: yup.string(),
        },
      ],
    },
    {
      type: FIELD_TYPES.INFO_FIELDS,
      id: 'info-column-4',
      fields: [
        {
          title: '#5 ' + svStrings.fields.teamInfoName,
          id: 'info-name-4',
          type: FIELD_TYPES.TEXT,
          key: 'info[4].name',
          value: '',
          validation: yup.string(),
        },
        {
          title: svStrings.fields.teamInfoValue,
          id: 'info-value-4',
          type: FIELD_TYPES.TEXT,
          key: 'info[4].value',
          value: '',
          validation: yup.string(),
        },
      ],
    },
    {
      type: FIELD_TYPES.INFO_FIELDS,
      id: 'info-column-5',
      fields: [
        {
          title: '#6 ' + svStrings.fields.teamInfoName,
          id: 'info-name-5',
          type: FIELD_TYPES.TEXT,
          key: 'info[5].name',
          value: '',
          validation: yup.string(),
        },
        {
          title: svStrings.fields.teamInfoValue,
          id: 'info-value-5',
          type: FIELD_TYPES.TEXT,
          key: 'info[5].value',
          value: '',
          validation: yup.string(),
        },
      ],
    },
    {
      type: FIELD_TYPES.INFO_FIELDS,
      id: 'info-column-6',
      fields: [
        {
          title: '#7 ' + svStrings.fields.teamInfoName,
          id: 'info-name-6',
          type: FIELD_TYPES.TEXT,
          key: 'info[6].name',
          value: '',
          validation: yup.string(),
        },
        {
          title: svStrings.fields.teamInfoValue,
          id: 'info-value-6',
          type: FIELD_TYPES.TEXT,
          key: 'info[6].value',
          value: '',
          validation: yup.string(),
        },
      ],
    },
    {
      type: FIELD_TYPES.INFO_FIELDS,
      id: 'info-column-7',
      fields: [
        {
          title: '#8 ' + svStrings.fields.teamInfoName,
          id: 'info-name-7',
          type: FIELD_TYPES.TEXT,
          key: 'info[7].name',
          value: '',
          validation: yup.string(),
        },
        {
          title: svStrings.fields.teamInfoValue,
          id: 'info-value-7',
          type: FIELD_TYPES.TEXT,
          key: 'info[7].value',
          value: '',
          validation: yup.string(),
        },
      ],
    },
  ];
};

// Disabled  with Antourage
// const createLiveStreamFields = () => {
//  return [
//    {
//      title: svStrings.fields.streamBackgroundCard,
//      id: 'background-card',
//      type: FIELD_TYPES.IMAGE,
//      key: 'clubStreamLiveCardBackground',
//      value: '',
//      validation: yup.string().url(svStrings.validations.validImage),
//    },
//    {
//      title: svStrings.fields.brandingClubStreams,
//      id: 'has-club-streams',
//      type: FIELD_TYPES.SWITCH,
//      key: 'hasClubstreams',
//      value: '',
//      validation: yup.boolean(),
//    },
//  ];
// }

const createVideoPlayerBrandingFields = () => {
  return [
    {
      title: svStrings.fields.brandingPartnerLogo,
      id: 'video-league-partner',
      type: FIELD_TYPES.IMAGE,
      key: 'videoPlayerLeaguePartner',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
    {
      title: svStrings.fields.brandingClubLogo,
      id: 'video-club-partner',
      type: FIELD_TYPES.IMAGE,
      key: 'videoPlayerClubPartner',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
  ];
};

const createSupportFields = () => {
  return [
    {
      title: svStrings.fields.ticketSupportEmail,
      helperText: svStrings.fields.ticketSupportEmailHelper,
      id: 'ticket-support-email',
      type: FIELD_TYPES.TEXT,
      key: 'support.ticketEmail',
      value: '',
      validation: yup.string().email(svStrings.validations.validEmail),
    },
    {
      title: svStrings.fields.infoEmail,
      helperText: svStrings.fields.infoEmailHelper,
      id: 'info-email',
      type: FIELD_TYPES.TEXT,
      key: 'support.infoEmail',
      value: '',
      validation: yup.string().email(svStrings.validations.validEmail),
    },
    {
      title: svStrings.fields.appEmail,
      helperText: svStrings.fields.appEmailHelper,
      id: 'app-email',
      type: FIELD_TYPES.TEXT,
      key: 'support.appEmail',
      value: '',
      validation: yup.string().email(svStrings.validations.validEmail),
    },
    // Disabled for now
    // {
    //   title: svStrings.fields.supportPhoneNumber,
    //   helperText: svStrings.fields.supportPhoneNumberHelper,
    //   id: 'support-phone-number',
    //   type: FIELD_TYPES.TEXT,
    //   key: 'support.phoneNumber',
    //   value: '',
    //   validation: yup.string().matches(/^\+?[\d\s\-()]*$/, svStrings.validations.validPhoneNumber),
    // },
  ];
};

const createNewsFields = () => {
  return [
    {
      title: svStrings.fields.newsRSSLink,
      id: 'news-rss-url',
      type: FIELD_TYPES.VALIDATE_RSS,
      key: 'newsRssUrl',
      value: '',
      validation: yup.boolean().isTrue(),
    },
  ];
};

const createArenaFields = () => {
  return [
    {
      title: svStrings.fields.arenaName,
      id: 'arena-name',
      type: FIELD_TYPES.TEXT,
      key: 'arena.name',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.arenaSpectators,
      id: 'arena-spectators',
      type: FIELD_TYPES.TEXT,
      key: 'arena.spectators',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.arenaBuilt,
      id: 'arena-built',
      type: FIELD_TYPES.TEXT,
      key: 'arena.built',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.arenaRecord,
      id: 'arena-record',
      type: FIELD_TYPES.TEXT,
      key: 'arena.record',
      value: '',
      validation: yup.string(),
    },
    {
      title: svStrings.fields.arenaOriginalImage,
      id: 'arena-original-image',
      type: FIELD_TYPES.IMAGE,
      key: 'arena.originalImageUrl',
      value: '',
      validation: yup.string().url(svStrings.validations.validImage),
    },
  ];
};

export const createInitialUIschema = (teamAbbrv: string, polls: Poll[]) => {
  return [
    {
      name: 'Promos',
      title: svStrings.fields.titlePromos,
      superAdminOnly: false,
      fields: createPromos(teamAbbrv, 18, polls),
    },
    {
      name: 'News',
      title: svStrings.fields.titleNews,
      superAdminOnly: false,
      fields: createNewsFields(),
      isTeamDataValue: true,
    },
    {
      name: 'Team Links',
      title: svStrings.fields.titleTeamLinks,
      superAdminOnly: false,
      isTeamDataValue: true,
      fields: createTeamLinkFields(),
    },
    {
      name: 'Application Theme',
      title: svStrings.fields.titleAppTheme,
      superAdminOnly: false,
      fields: createAppThemeFields(),
    },
    {
      name: 'Video player branding',
      title: svStrings.fields.titleVideoBranding,
      superAdminOnly: false,
      fields: createVideoPlayerBrandingFields(),
    },
    {
      name: 'Support',
      title: svStrings.fields.titleSupport,
      superAdminOnly: false,
      fields: createSupportFields(),
    },
    {
      name: 'General Team Information',
      title: svStrings.fields.titleTeam,
      superAdminOnly: false,
      fields: createTeamInfoFields(),
    },
    {
      name: 'Information',
      title: svStrings.fields.titleTeamInfo,
      superAdminOnly: false,
      isTeamDataValue: true,
      fields: createInfoFields(),
    },
    {
      name: 'Arena',
      title: svStrings.fields.titleArena,
      superAdminOnly: false,
      isTeamDataValue: true,
      fields: createArenaFields(),
    },
    /* Disabled with Antourage
    {
      name: 'Live Stream',
      title: svStrings.fields.titleLiveStream,
      superAdminOnly: false,
      fields: createLiveStreamFields(),
    },
    { TEMP Disabled
      name: 'Arena',
      title: svStrings.fields.titleArena,
      superAdminOnly: false,
      fields: [
        {
          title: svStrings.fields.arenaUpcomingImage,
          id: 'arena-upcoming-image',
          type: FIELD_TYPES.IMAGE,
          key: 'arena.upcomingImageUrl',
          value: '',
          validation: yup.string().url(svStrings.validations.validImage),
        },
        {
          title: svStrings.fields.arenaOriginalImage, // TODO Flytta CHECK!
          id: 'arena-original-image',
          type: FIELD_TYPES.IMAGE,
          key: 'arena.originalImageUrl',
          value: '',
          validation: yup.string().url(svStrings.validations.validImage),
        },
        {
          title: svStrings.fields.arenaMatchUpcomingImage,
          id: 'arena-matchcard-upcoming-image',
          type: FIELD_TYPES.IMAGE,
          key: 'arena.matchCardUpcomingImageUrl',
          value: '',
          validation: yup.string().url(svStrings.validations.validImage),
        },
        {
          title: svStrings.fields.arenaOngoingImage,
          id: 'arena-ongoing-image',
          type: FIELD_TYPES.IMAGE,
          key: 'arena.ongoingImageUrl',
          value: '',
          validation: yup.string().url(svStrings.validations.validImage),
        },
        {
          title: svStrings.fields.arenaFinishedImage,
          id: 'arena-finished-image',
          type: FIELD_TYPES.IMAGE,
          key: 'arena.finishedImageUrl',
          value: '',
          validation: yup.string().url(svStrings.validations.validImage),
        },
      ],
    },*/
    /* Disabled for now
    {
      name: 'League Sponsor',
      title: svStrings.fields.titleLeagueSponsor,
      superAdminOnly: false,
      fields: [
        {
          title: svStrings.fields.sponsorImage,
          id: 'league-sponsor-image',
          type: FIELD_TYPES.IMAGE,
          key: 'leagueSponsor.image',
          value: '',
          validation: yup.string().url(svStrings.validations.validImage),
        },
        {
          title: svStrings.fields.sponsorUrl,
          id: 'league-sponsor-target-url',
          type: FIELD_TYPES.URL,
          key: 'leagueSponsor.targeturl',
          value: '',
          validation: yup.string().url(svStrings.validations.validUrl),
        },
        {
          title: svStrings.fields.sponsorColor,
          id: 'league-sponsor-color',
          type: FIELD_TYPES.COLOR_PICKER,
          key: 'leagueSponsor.color',
          value: '',
          validation: yup.string(),
        },
      ],
    },*/
    /* Removed {
      name: 'Technical Setup',
      title: svStrings.fields.titleSetup,
      superAdminOnly: true,
      isTeamDataValue: true,
      isReadOnly: true,
      fields: [
        {
          title: 'Match card Cache Key',
          id: 'match-card-cache-key',
          type: FIELD_TYPES.TEXT,
          key: 'matchCardCacheKey',
          value: '',
        },
        {
          title: 'Every Sport ID',
          id: 'every-sport-id',
          type: FIELD_TYPES.NUMBER,
          key: 'everySportId',
          value: '',
        },
        {
          title: 'Unibet ID',
          id: 'unibet-id',
          type: FIELD_TYPES.NUMBER,
          key: 'unibetId',
          value: '',
        },
        {
          title: 'Eliteprospects ID',
          id: 'eliteprospects-id',
          type: FIELD_TYPES.NUMBER,
          key: 'epId',
          value: '',
        },
        {
          title: 'Fogis ID',
          id: 'fogis-id',
          type: FIELD_TYPES.NUMBER,
          key: 'fogisId',
          value: '',
        },
        {
          title: 'Forzasys ID',
          id: 'forzasys-id',
          type: FIELD_TYPES.NUMBER,
          key: 'forzasysId',
          value: '',
        },
        {
          title: 'Forzasys Import ID',
          id: 'forzasys-import-id',
          type: FIELD_TYPES.NUMBER,
          key: 'forzasysImportId',
          value: '',
        },
        {
          title: 'Forzasys Import Source',
          id: 'forzasys-import-source',
          type: FIELD_TYPES.TEXT,
          key: 'forzasysImportSource',
          value: '',
        },
        {
          title: 'Hash',
          id: 'hash',
          type: FIELD_TYPES.TEXT,
          key: 'hash',
          value: '',
        },
        {
          title: 'ID',
          id: 'id',
          type: FIELD_TYPES.TEXT,
          key: 'id',
          value: '',
        },
      ],
    },*/
  ] satisfies UIschema[];
};
